import React from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1e293b]/90 backdrop-blur-md border border-white/10 p-4 rounded-xl shadow-2xl">
                <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2 mb-1 last:mb-0">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-gray-300 text-sm font-medium">{entry.name}:</span>
                        <span className="text-white text-sm font-bold font-mono">{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const DetailedDesignScreen = React.memo(({ players, analyticsData, onBack }) => {
    if (!analyticsData || !players) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-400 font-medium animate-pulse">Loading analytics...</div>
            </div>
        );
    }

    const { performance, bidsVsWon, bags } = analyticsData;

    return (
        <div className="min-h-screen bg-[#1a2332] font-['Inter'] text-white flex flex-col relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

            <main className="relative z-10 flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
                {/* Bid Accuracy Chart (New) */}
                <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl">
                    <h3 className="font-bold text-lg uppercase tracking-wider mb-6 text-gray-300 flex items-center gap-2">
                        <span className="w-1 h-6 bg-gradient-to-b from-emerald-400 to-teal-600 rounded-full"></span>
                        Bid Accuracy
                    </h3>
                    <div className="h-64 md:h-96 w-full">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={20}>
                            <BarChart data={bidsVsWon} barGap={8}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} dy={10} />
                                <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} dx={-10} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                                <defs>
                                    <linearGradient id="bidsGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#818cf8" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#4f46e5" stopOpacity={1} />
                                    </linearGradient>
                                    <linearGradient id="wonGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
                                        <stop offset="100%" stopColor="#059669" stopOpacity={1} />
                                    </linearGradient>
                                </defs>
                                <Bar dataKey="Bids" fill="url(#bidsGradient)" radius={[6, 6, 0, 0]} maxBarSize={50} />
                                <Bar dataKey="Won" fill="url(#wonGradient)" radius={[6, 6, 0, 0]} maxBarSize={50} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {/* Score Efficiency (Points per Hand) */}
                    <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl">
                        <h3 className="font-bold text-lg uppercase tracking-wider mb-6 text-gray-300 flex items-center gap-2">
                            <span className="w-1 h-6 bg-gradient-to-b from-amber-400 to-orange-600 rounded-full"></span>
                            Score Trajectory
                        </h3>
                        <div className="h-64 md:h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={20}>
                                <AreaChart data={performance}>
                                    <defs>
                                        {players.map((player, index) => (
                                            <linearGradient key={player.name} id={`color${index}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={player.color} stopOpacity={0.3} />
                                                <stop offset="95%" stopColor={player.color} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                    <XAxis dataKey="hand" stroke="#94A3B8" tick={{ fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} dy={10} />
                                    <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} dx={-10} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                                    {players.map((player, index) => (
                                        <Area
                                            key={player.name}
                                            type="monotone"
                                            dataKey={player.name}
                                            stroke={player.color}
                                            strokeWidth={3}
                                            fillOpacity={1}
                                            fill={`url(#color${index})`}
                                        />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Bag Accumulation */}
                    <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl">
                        <h3 className="font-bold text-lg uppercase tracking-wider mb-6 text-gray-300 flex items-center gap-2">
                            <span className="w-1 h-6 bg-gradient-to-b from-red-400 to-pink-600 rounded-full"></span>
                            Bag Collection
                        </h3>
                        <div className="h-64 md:h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={20}>
                                <BarChart data={bags}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                    <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} dy={10} />
                                    <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fontWeight: 'bold' }} axisLine={false} tickLine={false} dx={-10} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                                    <defs>
                                        <linearGradient id="bagsGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#fbbf24" stopOpacity={1} />
                                            <stop offset="100%" stopColor="#d97706" stopOpacity={1} />
                                        </linearGradient>
                                    </defs>
                                    <Bar dataKey="Bags" fill="url(#bagsGradient)" radius={[6, 6, 0, 0]} maxBarSize={50} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
});

export default DetailedDesignScreen;
